import type { ThunkAction, ThunkExtras, GetState, APIError, Dispatch } from '../../types/app.types';
import type { V2Pharmacy } from '../../types/api-v2.types';
import { trackEvent } from '../../utils/analytics';
export type TransferPharmacy = {
  key: string;
  displayName: string;
  id: number;
};
export const TRANSFER_FROM_PHARMACIES: TransferPharmacy[] = [
  {
    key: 'cvs',
    displayName: 'CVS',
    id: 497854,
  },
  {
    key: 'walgreens',
    displayName: 'Walgreens',
    id: 512683,
  },
  {
    key: 'duanereade',
    displayName: 'Duane Reade',
    id: 3,
  },
  {
    key: 'riteaid',
    displayName: 'Rite Aid',
    id: 2,
  },
];
export type SetTransferFromPharmacyChain = {
  type: 'transfer/SET_FROM_PHARMACY_CHAIN';
  pharmacyChain: TransferPharmacy;
};
export function setTransferFromPharmacyChain(pharmacyChain: TransferPharmacy): SetTransferFromPharmacyChain {
  return {
    type: 'transfer/SET_FROM_PHARMACY_CHAIN',
    pharmacyChain,
  };
}
export type SetTransferFromPharmacy = {
  type: 'transfer/SET_FROM_PHARMACY';
  pharmacy: V2Pharmacy;
};
export function setTransferFromPharmacy(pharmacy: V2Pharmacy): SetTransferFromPharmacy {
  return {
    type: 'transfer/SET_FROM_PHARMACY',
    pharmacy,
  };
}
export type SetTransferToPharmacy = {
  type: 'transfer/SET_TO_PHARMACY';
  pharmacy: V2Pharmacy;
};
export function setTransferToPharmacy(pharmacy: V2Pharmacy): SetTransferToPharmacy {
  return {
    type: 'transfer/SET_TO_PHARMACY',
    pharmacy,
  };
}
export type CheckTransferEligibilitySuccess = {
  type: 'transfer/CHECK_ELIGIBILITY_SUCCESS';
};
export type CheckTransferEligibilityError = {
  type: 'transfer/CHECK_ELIGIBILITY_ERROR';
} & APIError;
export function isEligibileForTransfer(useClaims: boolean, usePurchases: boolean): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .get('/pharmacies/transfer/eligibility', {
        params: {
          use_claims: useClaims,
          use_purchases: usePurchases,
        },
      })
      .then(() =>
        dispatch({
          type: 'transfer/CHECK_ELIGIBILITY_SUCCESS',
        })
      )
      .catch((err: APIError) => {
        return dispatch({
          type: 'transfer/CHECK_ELIGIBILITY_ERROR',
          ...err,
        });
      });
  };
}
export type InitiateTransferSuccess = {
  type: 'transfer/INITIATE_SUCCESS';
};
export type InitiateTransferError = {
  type: 'transfer/INITIATE_ERROR';
} & APIError;
export function initiateTransfer(
  toPharmacyNpi: string,
  fromPharmacyNpi: string,
  useClaims: boolean,
  usePurchases: boolean
): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post('/pharmacies/transfer/initiate', {
        pharmacy_npi: toPharmacyNpi,
        previous_pharmacy_npi: fromPharmacyNpi,
        source: 'pwa',
        use_claims: useClaims,
        use_purchases: usePurchases,
      })
      .then(() => {
        dispatch(trackEvent('Started Transfer'));
        dispatch({
          type: 'transfer/INITIATE_SUCCESS',
        });
      })
      .catch((err: APIError) => {
        return dispatch({
          type: 'transfer/INITIATE_ERROR',
          ...err,
        });
      });
  };
}
export type TransferAction =
  | SetTransferFromPharmacyChain
  | SetTransferFromPharmacy
  | SetTransferToPharmacy
  | InitiateTransferSuccess
  | InitiateTransferError
  | CheckTransferEligibilityError
  | CheckTransferEligibilitySuccess;
export type TransferState = {
  fromPharmacyChain: TransferPharmacy | null | undefined;
  fromPharmacy: V2Pharmacy | null | undefined;
  toPharmacy: V2Pharmacy | null | undefined;
  isEligible: boolean;
};
const initialState: TransferState = {
  fromPharmacyChain: null,
  fromPharmacy: null,
  toPharmacy: null,
  isEligible: false,
};
export default function transferReducer(state: TransferState = initialState, action: TransferAction): TransferState {
  switch (action.type) {
    case 'transfer/SET_FROM_PHARMACY_CHAIN':
      return { ...state, fromPharmacyChain: action.pharmacyChain };

    case 'transfer/SET_FROM_PHARMACY':
      return { ...state, fromPharmacy: action.pharmacy };

    case 'transfer/SET_TO_PHARMACY':
      return { ...state, toPharmacy: action.pharmacy };

    case 'transfer/CHECK_ELIGIBILITY_SUCCESS':
      return { ...state, isEligible: true };

    case 'transfer/CHECK_ELIGIBILITY_ERROR':
      return { ...state, isEligible: false };

    default:
      return state;
  }
}
